import React, { useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';

import properties from '../../../../../modules/ajio-util/properties';
import styles from './index.pcss';
import Filters from './Filters';
import productsUtil, {
  parseProductQueryString,
  stringifyFacets
} from '../../../../../utils/products';
import queryUtil from '../../../../../modules/ajio-util/query';
import { getCurrentStore } from '../../../../selectors/metaData';
import { getLuxeFlags } from '../../../../../Flags/selectors';
import { buildFiltersArray, fireFilterApplied, fireFilterRemoved } from '../../analytics';
import { showLoader } from '../../../../../actions/shared/loader.js';
import { runAfterPaint } from '../../../../utils/deferAnalytics';

const cx = classNames.bind(styles);

const INLINE_CODES = new Set(['tags', 'sla']);

interface QuickFilter {
  code: string;
  name: string;
  tabPosition: number;
  count: number;
  selected: boolean;
  selectedOrder: number | null;
}

interface QuickFilterTag {
  facetCode: string;
  valueCode: string;
  valueName: string;
  selected: boolean;
}

type SortableItem =
  | {
      type: 'filter';
      key: string;
      tabPosition: number;
      selected: boolean;
      selectedOrder: number | null;
      filter: QuickFilter;
    }
  | {
      type: 'inlineTag';
      key: string;
      tabPosition: number;
      selected: boolean;
      selectedOrder: number | null;
      tag: QuickFilterTag;
    };

interface FacetValue {
  code: string;
  name: string;
  query?: { query?: { value?: string } };
}

interface FacetItem {
  code: string;
  name: string;
  values?: FacetValue[];
}

const QuickFilters = props => {
  const {
    currentStore = '',
    freeTextSearch = '',
    request = {},
    facets = {},
    luxeFlags = {},
    visualFiltersConfig = {},
    replaceInHistory,
    setDisplaySortModal,
    setDisplayOtherFilterModal,
    setSelectedFilterTabCode,
    updateFilterModal = () => {},
    dispatch
  } = props;
  const { query: requestQuery = {} } = request as any;
  const search_term: string = requestQuery.text || freeTextSearch || '';

  const { filterIcons = {}, quickFilterOrder = [] } = visualFiltersConfig || {};
  const { plpGender = '', currentFacets = {}, quickFilters = [] } = facets || {};
  const { allfacetsItems = [], entities: currentEntities = {} } = currentFacets || {};

  // if (!quickFilters || quickFilters.length === 0) return null;

  const queryString = queryUtil.parseSearch(location.search) || {};
  const query = parseProductQueryString(queryString.query || '');
  const { facets: appliedFacets = '' } = query;
  const initialFacetsQuery = productsUtil.parseFacets(appliedFacets) as Record<string, string[]>;

  const effectiveFacetsQuery = useMemo(() => {
    const enriched: Record<string, string[]> = { ...initialFacetsQuery };
    Object.keys(currentEntities).forEach(entityId => {
      const entity = currentEntities[entityId] || {};
      if (!entity.childrens?.length || !entity.code) return;
      const facetCode = entity.code;
      const currentValues = enriched[facetCode] || [];
      const apiSelected: string[] = [];
      entity.childrens.forEach((childId: string) => {
        const child = currentEntities[childId] || {};
        if (child.selected && child.code && !currentValues.includes(child.code)) {
          apiSelected.push(child.code);
        }
      });
      if (apiSelected.length) {
        enriched[facetCode] = [...currentValues, ...apiSelected];
      }
    });
    return enriched;
  }, [appliedFacets, currentEntities]);

  const { isThemeEnable } = luxeFlags;
  const luxe = isThemeEnable === 'ON' && currentStore === 'luxe';
  const fleek = String(currentStore).toLowerCase() === 'ajiogram';

  const facetCodesSet = useMemo(
    () => new Set(allfacetsItems.map((item: { code: string }) => item.code)),
    [allfacetsItems]
  );

  const quickFilterMetaMap = useMemo(() => {
    const map: Record<string, { name: string; tabPosition: number }> = {};
    for (const f of quickFilterOrder) {
      map[f.code] = { name: f.name, tabPosition: f.tabPosition };
    }
    return map;
  }, []);

  const inlineTagsByCode: Record<string, QuickFilterTag[]> = useMemo(() => {
    const result: Record<string, QuickFilterTag[]> = {};

    for (const item of quickFilters) {
      if (!INLINE_CODES.has(item.code)) continue;

      const matchingFacet = allfacetsItems.find((facet: FacetItem) => facet.code === item.code);

      if (item.code === 'sla') {
        // Show all SLA values from quickFilter config; check selected state from facets
        const matched: QuickFilterTag[] = [];
        const coveredCodes = new Set<string>();
        for (const valueCode of item.values) {
          const facetValue = matchingFacet?.values?.find((fv: FacetValue) => fv.code === valueCode);
          const isSelected = effectiveFacetsQuery['sla']?.includes(valueCode) || false;
          matched.push({
            facetCode: 'sla',
            valueCode,
            valueName: facetValue?.name || valueCode,
            selected: isSelected
          });
          coveredCodes.add(valueCode);
        }
        // Also add any selected SLA values from facets not present in item.values
        const selectedSlaCodes = effectiveFacetsQuery['sla'] || [];
        for (const valueCode of selectedSlaCodes) {
          if (!coveredCodes.has(valueCode)) {
            const facetValue = matchingFacet?.values?.find((fv: FacetValue) => fv.code === valueCode);
            matched.push({
              facetCode: 'sla',
              valueCode,
              valueName: facetValue?.name || valueCode,
              selected: true
            });
          }
        }
        if (matched.length > 0) {
          result['sla'] = matched;
        }
        continue;
      }

      if (!matchingFacet?.values) continue;

      const matched: QuickFilterTag[] = [];
      for (const valueName of item.values) {
        const facetValue = matchingFacet.values.find((fv: FacetValue) => fv.code === valueName);
        if (facetValue) {
          const isSelected = effectiveFacetsQuery[item.code]?.includes(facetValue.code) || false;
          matched.push({
            facetCode: item.code,
            valueCode: facetValue.code,
            valueName: facetValue.name,
            selected: isSelected
          });
        }
      }

      if (matched.length > 0) {
        result[item.code] = matched;
      }
    }

    // If SLA not in quickFilters but is selected in facets, build tags from allfacetsItems
    if (!result['sla']) {
      const selectedSlaCodes = effectiveFacetsQuery['sla'] || [];
      if (selectedSlaCodes.length > 0) {
        const slaFacet = allfacetsItems.find((facet: FacetItem) => facet.code === 'sla');
        const matched: QuickFilterTag[] = selectedSlaCodes.map(code => {
          const facetValue = slaFacet?.values?.find((fv: FacetValue) => fv.code === code);
          return {
            facetCode: 'sla',
            valueCode: code,
            valueName: facetValue?.name || code,
            selected: true
          };
        });
        result['sla'] = matched;
      }
    }

    return result;
  }, [allfacetsItems, effectiveFacetsQuery]);

  const filters: QuickFilter[] = useMemo(() => {
    const facetKeys = Object.keys(effectiveFacetsQuery);
    const showGender = !(plpGender && plpGender.toLowerCase() !== 'general');
    const result: QuickFilter[] = [];

    // INLINE_CODES (e.g. sla, tags) come from the API response
    for (const apiFilter of quickFilters) {
      if (!INLINE_CODES.has(apiFilter.code)) continue;
      const meta = quickFilterMetaMap[apiFilter.code];
      if (!meta) continue;
      if (!inlineTagsByCode[apiFilter.code]) continue;

      const facetValues = effectiveFacetsQuery[apiFilter.code];
      const isSelected = Array.isArray(facetValues) && facetValues.length > 0;

      result.push({
        code: apiFilter.code,
        name: meta.name,
        tabPosition: meta.tabPosition,
        count: isSelected ? facetValues.length : 0,
        selected: isSelected,
        selectedOrder: isSelected ? facetKeys.indexOf(apiFilter.code) + 1 : null
      });
    }

    // If SLA not in quickFilters but is selected in facets, add it from inlineTagsByCode
    const slaAlreadyAdded = result.some(f => f.code === 'sla');
    if (!slaAlreadyAdded && inlineTagsByCode['sla']) {
      const meta = quickFilterMetaMap['sla'];
      if (meta) {
        const facetValues = effectiveFacetsQuery['sla'];
        const isSelected = Array.isArray(facetValues) && facetValues.length > 0;
        if (isSelected) {
          result.push({
            code: 'sla',
            name: meta.name,
            tabPosition: meta.tabPosition,
            count: facetValues.length,
            selected: true,
            selectedOrder: facetKeys.indexOf('sla') + 1
          });
        }
      }
    }

    // Non-inline filters come from quickFilterOrder + entities
    for (const filterMeta of quickFilterOrder) {

      const { code, name, tabPosition } = filterMeta;

      if (INLINE_CODES.has(code)) continue; // already handled above
      if (code === 'genderfilter' && !showGender) continue;

      const facetValues = code === 'pricerange'
        ? (effectiveFacetsQuery['price'] || effectiveFacetsQuery[code])
        : effectiveFacetsQuery[code];
      const isSelected = Array.isArray(facetValues) && facetValues.length > 0;

      if (!facetCodesSet.has(code) && !isSelected) continue;

      result.push({
        code,
        name,
        tabPosition,
        count: isSelected && code !== 'pricerange' ? facetValues.length : 0,
        selected: isSelected,
        selectedOrder: isSelected ? facetKeys.indexOf(code === 'pricerange' ? 'price' : code) + 1 : null
      });
    }

    return result;
  }, [
    effectiveFacetsQuery,
    plpGender,
    facetCodesSet,
    inlineTagsByCode,
    quickFilters,
    quickFilterOrder,
    quickFilterMetaMap
  ]);

  const handleFilterClick = (filterCode: string) => {
    if (filterCode === 'allfilters') {
      // Same as bottom FILTERS (Filter.js): open after the next paint so the drawer mount
      // and the hash-triggered GTM History Change run outside the tap.
      runAfterPaint(() => {
        updateFilterModal(true);
        window.location.hash = '#filterBy';
      });
      return;
    }

    if (filterCode === 'sort') {
      setDisplaySortModal(true);
    } else {
      runAfterPaint(() => {
        setDisplayOtherFilterModal(true);
        setSelectedFilterTabCode(filterCode);
      });
    }
  };

  const handleInlineTagClick = (facetCode: string, valueCode: string, valueName: string = '') => {
    const updated = { ...effectiveFacetsQuery };
    const values = updated[facetCode] || [];
    const isRemoving = values.includes(valueCode);

    const tagKey = `${facetCode}-${valueCode}`;
    const visualPosition = scrollableItems.findIndex(item => item.key === tagKey) + 1;

    if (isRemoving) {
      updated[facetCode] = values.filter(v => v !== valueCode);
      if (updated[facetCode].length === 0) delete updated[facetCode];
      fireFilterRemoved(
        'visual',
        buildFiltersArray({ [facetCode]: [valueCode] }, allfacetsItems, 2, visualPosition),
        {
          screen_title: freeTextSearch,
          search_term,
          raw_query: search_term || ''
        }
      );
    } else {
      // For SLA, selecting a new value deselects any previously selected SLA (single-select)
      updated[facetCode] = facetCode === 'sla' ? [valueCode] : [...values, valueCode];
      fireFilterApplied(
        'visual',
        buildFiltersArray({ [facetCode]: [valueCode] }, allfacetsItems, 2, visualPosition),
        {
          screen_title: freeTextSearch,
          search_term,
          raw_query: search_term || ''
        }
      );
    }

    dispatch(showLoader('visual-filter-loader'));
    replaceInHistory?.({
      currentPage: 0,
      facets: stringifyFacets(updated)
    });
  };

  const sortedItems: SortableItem[] = useMemo(() => {
    const facetKeys = Object.keys(effectiveFacetsQuery);
    const items: SortableItem[] = [];

    for (const filter of filters) {
      if (INLINE_CODES.has(filter.code) && inlineTagsByCode[filter.code]) {
        const appliedValues = effectiveFacetsQuery[filter.code] || [];
        inlineTagsByCode[filter.code].forEach((tag, idx) => {
          const valueIndex = appliedValues.indexOf(tag.valueCode);
          const isSelected = valueIndex !== -1;
          items.push({
            type: 'inlineTag',
            key: `${tag.facetCode}-${tag.valueCode}`,
            tabPosition: filter.tabPosition + idx * 0.01,
            selected: isSelected,
            selectedOrder: isSelected
              ? facetKeys.indexOf(tag.facetCode) + 1 + valueIndex * 0.01
              : null,
            tag: { ...tag, selected: isSelected }
          });
        });
      } else {
        items.push({
          type: 'filter',
          key: filter.code,
          tabPosition: filter.tabPosition,
          selected: filter.selected,
          selectedOrder: filter.selectedOrder,
          filter
        });
      }
    }

    return items.sort((a, b) => {
      if (a.selected && b.selected) {
        return (b.selectedOrder ?? 0) - (a.selectedOrder ?? 0);
      }

      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;

      return a.tabPosition - b.tabPosition;
    });
  }, [filters, inlineTagsByCode, effectiveFacetsQuery]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [appliedFacets]);

  const scrollableItems = sortedItems;
  const selectedItemCount = scrollableItems?.filter(item => item?.selected)?.length;

  return (
    <div className={cx('quick-filters-wrapper', { ajiogram: fleek })}>
      {scrollableItems.length > 0 && (
        <div
          className={cx('filter-item', 'all-filters-fixed', {
            selected: selectedItemCount > 0,
            luxe,
            ajiogram: fleek
          })}
          onClick={() => handleFilterClick('allfilters')}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleFilterClick('allfilters');
            }
          }}
        >
          <img
            src={`${properties.getProperty('imageBaseUrl')}/static/img/${
              fleek ? 'fleek-filter-icon.svg' : 'filter_icon_black.svg'
            }`}
            alt="Filter icon"
            className={cx('caret-img')}
            aria-hidden="true"
          />
          {selectedItemCount > 0 && (
            <span className={cx('main-filter-count', { ajiogram: fleek })}>
              {selectedItemCount}
            </span>
          )}
        </div>
      )}
      <div ref={containerRef} className={cx('quick-filters-scrollable')}>
        {scrollableItems.map(item => {
          if (item.type === 'inlineTag') {
            const { tag } = item;
            const iconUrl = filterIcons[tag.valueCode.toLowerCase()];
            return (
              <div
                key={item.key}
                className={cx('filter-item', {
                  selected: tag.selected,
                  luxe,
                  ajiogram: fleek
                })}
                onClick={() => handleInlineTagClick(tag.facetCode, tag.valueCode, tag.valueName)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleInlineTagClick(tag.facetCode, tag.valueCode, tag.valueName);
                  }
                }}
              >
                {iconUrl && (
                  <img
                    src={iconUrl}
                    alt={tag.valueName}
                    className={cx('tag-icon')}
                    aria-hidden="true"
                  />
                )}
                {tag.valueName}
              </div>
            );
          }

          const { filter } = item;
          return (
            <div
              key={item.key}
              className={cx('filter-item', {
                selected: filter.selected,
                luxe,
                ajiogram: fleek
              })}
              onClick={() => handleFilterClick(filter.code)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleFilterClick(filter.code);
                }
              }}
            >
              {filter.name}
              {filter.count > 0 && (
                <span className={cx({ ajiogram: fleek })}>{`(${filter.count})`}</span>
              )}
              <img
                src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                  fleek ? 'fleek_quick_filter_arrow_down.svg' : 'quick_filter_arrow_down.svg'
                }`}
                alt="Caret icon"
                aria-hidden="true"
                className={cx('caret-img')}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const { facets = {}, request = {} } = state || {};

  return {
    facets,
    request,
    luxeFlags: getLuxeFlags(state?.flags),
    currentStore: getCurrentStore(state),
    freeTextSearch: state?.productsAdditionalInfo?.freeTextSearch
  };
};

export default connect(mapStateToProps)(QuickFilters);
